// ============================================================
// VeilleView.jsx — Onglet "Veille jurisprudentielle" pour Hard to Value
// Vue top-level (comme JuridictionView), lecture seule, FR/EN.
// Charge le corpus une fois (client-side filtering) puis filtre/recherche/trie
// en navigateur. 2 sous-onglets : Liste | Lignée par thème + vue Détail plein écran.
// ============================================================
// INTÉGRATION dans App.jsx :
//   1. import VeilleView from "./VeilleView";
//   2. nav : <button onClick={()=>setView("veille")}>📚 {L.nav_veille}</button>
//   3. routing : {view==="veille" && <VeilleView L={L} lang={lang} onBack={()=>setView("dash")}/>}
//   4. clés TR.fr/TR.en : nav_veille
// ============================================================

import { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { P } from "./palette";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ── 8 thèmes : libellé EXACT (= valeur en base) + couleur de pastille + court ──
const THEME_META = [
  { label: "Titres de sociétés non cotées",                     color: "#C9A84C", fr: "Titres non cotés",        en: "Unlisted shares" },
  { label: "Goodwill et survaleurs",                            color: "#C9A84C", fr: "Goodwill",                en: "Goodwill" },
  { label: "Marques",                                           color: "#ec4899", fr: "Marques",                 en: "Trademarks" },
  { label: "Brevets et savoir-faire technique",                 color: "#14b8a6", fr: "Brevets / savoir-faire",  en: "Patents / know-how" },
  { label: "Clientèle et fonds de commerce",                    color: "#f59e0b", fr: "Clientèle / fonds",       en: "Goodwill / business" },
  { label: "Appareil industriel et immobilier d'exploitation",  color: P.textMuted, fr: "Appareil industriel",     en: "Industrial assets" },
  { label: "Redevances et prix de transfert sur incorporels",   color: "#ef4444", fr: "PT sur incorporels",      en: "TP on intangibles" },
  { label: "Prix de transfert hors incorporels",                color: "#f97316", fr: "PT hors incorporels",     en: "TP excl. intangibles" },
];
const themeMeta = (label) =>
  THEME_META.find((x) => x.label === label) || { label, color: P.textMuted, fr: label, en: label };

// ── Famille de cour — RÉPLIQUE EXACTE de classify_juridiction (run_bootstrap.py).
//    Même ordre (CE → CAA → Cass → CA), mêmes préfixes, variantes d'apostrophe. ──
function classifyJuridiction(label) {
  const low = (label || "").toLowerCase();
  if (low.startsWith("conseil d'") || low.startsWith("conseil d’")) return "CE";
  if (low.startsWith("caa") || low.includes("administrative d'appel")) return "CAA";
  if (low.includes("cour de cassation")) return "CASS_COM";
  if (low.includes("cour d'appel") || low.includes("cour d’appel")) return "CA_COM";
  return "autre";
}
const COURT_FAMILIES = [
  { key: "CE",       fr: "Conseil d'État",              en: "Conseil d'État" },
  { key: "CAA",      fr: "Cour administrative d'appel", en: "Administrative Court of Appeal" },
  { key: "CASS_COM", fr: "Cour de cassation",           en: "Court of Cassation" },
  { key: "CA_COM",   fr: "Cour d'appel",                en: "Court of Appeal" },
];
// ── Portée → couleur + libellé (la lignée se lit dans le temps) ──
const PORTEE_META = {
  "renforce":  { color: "#22c55e", fr: "Renforce",  en: "Reinforces" },
  "infléchit": { color: "#f59e0b", fr: "Infléchit", en: "Shifts" },
  "contredit": { color: "#ef4444", fr: "Contredit", en: "Contradicts" },
  "ouvre":     { color: "#C9A84C", fr: "Ouvre",     en: "Opens" },
};
const porteeMeta = (p) => {
  if (!p) return null;
  const k = p.toLowerCase().replace("inflechit", "infléchit");
  return PORTEE_META[k] || null;
};

// ── i18n local (le composant est autonome ; seul nav_veille vit dans TR) ──
const TXT = {
  fr: {
    title: "Veille jurisprudentielle", subtitle: "Corpus de jurisprudence française sur la valorisation",
    tab_liste: "Liste", tab_lignee: "Lignée par thème",
    f_theme: "Thème", f_court: "Cour", f_from: "Du", f_to: "Au", f_search: "Rechercher (parties, faits, solution…)",
    all_themes: "Tous les thèmes", all_courts: "Toutes les cours",
    count: (n, total) => `${n} fiche${n > 1 ? "s" : ""} sur ${total}`,
    c_date: "Date", c_court: "Juridiction", c_num: "N°", c_parties: "Parties", c_theme: "Thème", c_portee: "Portée",
    loading: "Chargement du corpus…", error: "Erreur de chargement", empty: "Aucune fiche ne correspond aux filtres.",
    back_app: "← Retour", back_list: "← Retour à la liste",
    d_formation: "Formation", d_parties: "Parties", d_theme: "Thème principal", d_secondaires: "Thèmes secondaires",
    d_actif: "Actif valorisé", d_methodes: "Méthodes de valorisation", d_faits: "Faits", d_solution: "Solution",
    d_lignee: "Positionnement dans la lignée", d_portee: "Portée", d_legifrance: "Consulter sur Légifrance ↗",
    d_troncature: "⚠ Texte signalé comme tronqué par Légifrance — synthèse possiblement incomplète.",
    lig_pick: "Thème de la lignée", lig_legend: "Portée :", lig_empty: "Aucune fiche pour ce thème.",
    lig_hint: "Du plus ancien au plus récent — l'évolution de la lignée se lit de haut en bas.",
    note_title: "Note de lignée", note_generate: "Générer une note", note_generating: "Génération…",
    note_generating_hint: "Génération en cours — 30 à 60 s selon le thème. Ne ferme pas l'onglet.",
    note_sec: "inclure thèmes secondaires", note_sec_short: "secondaires", note_history: "Historique",
    note_fiches: "fiches", note_perimetre: "Périmètre", note_download: "⬇ Télécharger .md", note_close: "Fermer",
    note_err: "Échec de génération", yes: "oui", no: "non",
    none: "—",
  },
  en: {
    title: "Case-law watch", subtitle: "French valuation case-law corpus",
    tab_liste: "List", tab_lignee: "Lineage by theme",
    f_theme: "Theme", f_court: "Court", f_from: "From", f_to: "To", f_search: "Search (parties, facts, holding…)",
    all_themes: "All themes", all_courts: "All courts",
    count: (n, total) => `${n} record${n > 1 ? "s" : ""} of ${total}`,
    c_date: "Date", c_court: "Court", c_num: "No.", c_parties: "Parties", c_theme: "Theme", c_portee: "Bearing",
    loading: "Loading corpus…", error: "Loading error", empty: "No record matches the filters.",
    back_app: "← Back", back_list: "← Back to list",
    d_formation: "Bench", d_parties: "Parties", d_theme: "Main theme", d_secondaires: "Secondary themes",
    d_actif: "Valued asset", d_methodes: "Valuation methods", d_faits: "Facts", d_solution: "Holding",
    d_lignee: "Position in the lineage", d_portee: "Bearing", d_legifrance: "View on Légifrance ↗",
    d_troncature: "⚠ Text flagged as truncated by Légifrance — synthesis may be incomplete.",
    lig_pick: "Lineage theme", lig_legend: "Bearing:", lig_empty: "No record for this theme.",
    lig_hint: "Oldest to newest — the lineage reads top to bottom.",
    note_title: "Lineage note", note_generate: "Generate a note", note_generating: "Generating…",
    note_generating_hint: "Generating — 30 to 60 s depending on the theme. Don't close the tab.",
    note_sec: "include secondary themes", note_sec_short: "secondary", note_history: "History",
    note_fiches: "records", note_perimetre: "Scope", note_download: "⬇ Download .md", note_close: "Close",
    note_err: "Generation failed", yes: "yes", no: "no",
    none: "—",
  },
};

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return lang === "fr" ? `${d}/${m}/${y}` : `${y}-${m}-${d}`;
};

const fmtDateTime = (iso, lang) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { dateStyle: "short", timeStyle: "short" });
};

// Rendu markdown stylé pour la note de lignée (thème dark slate de l'app).
const MD = {
  h1: ({ node, children, ...p }) => <h1 style={{ fontSize: 18, color: P.text, fontWeight: 700, margin: "0 0 8px" }} {...p}>{children}</h1>,
  h2: ({ node, children, ...p }) => <h2 style={{ fontSize: 15, color: P.text, fontWeight: 700, marginTop: 22, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid rgba(255,255,255,0.08)" }} {...p}>{children}</h2>,
  h3: ({ node, children, ...p }) => <h3 style={{ fontSize: 13, color: "#cbd5e1", fontWeight: 700, marginTop: 14, marginBottom: 6 }} {...p}>{children}</h3>,
  p: ({ node, ...p }) => <p style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.7, margin: "6px 0" }} {...p} />,
  strong: ({ node, ...p }) => <strong style={{ color: P.text, fontWeight: 700 }} {...p} />,
  em: ({ node, ...p }) => <em style={{ color: P.textSec }} {...p} />,
  a: ({ node, children, ...p }) => <a style={{ color: "#C9A84C" }} target="_blank" rel="noopener noreferrer" {...p}>{children}</a>,
  ul: ({ node, ...p }) => <ul style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.7, paddingLeft: 20, margin: "6px 0" }} {...p} />,
  ol: ({ node, ...p }) => <ol style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.7, paddingLeft: 20, margin: "6px 0" }} {...p} />,
  li: ({ node, ...p }) => <li style={{ margin: "3px 0" }} {...p} />,
  blockquote: ({ node, ...p }) => <blockquote style={{ borderLeft: "3px solid #f59e0b", background: "rgba(245,158,11,0.08)", padding: "8px 12px", margin: "10px 0", color: "#fbbf24", fontSize: 12 }} {...p} />,
  hr: () => <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.08)", margin: "16px 0" }} />,
  table: ({ node, ...p }) => <div style={{ overflowX: "auto" }}><table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11, margin: "8px 0" }} {...p} /></div>,
  th: ({ node, ...p }) => <th style={{ border: "1px solid rgba(255,255,255,0.12)", padding: "5px 8px", background: "rgba(255,255,255,0.04)", color: P.textSec, textAlign: "left", fontWeight: 600 }} {...p} />,
  td: ({ node, ...p }) => <td style={{ border: "1px solid rgba(255,255,255,0.08)", padding: "5px 8px", color: "#cbd5e1", verticalAlign: "top" }} {...p} />,
  code: ({ node, ...p }) => <code style={{ background: "rgba(255,255,255,0.06)", padding: "1px 4px", borderRadius: 3, fontSize: 11 }} {...p} />,
};

// ── styles partagés (thème dark slate de l'app) ──
const S = {
  wrap: { paddingLeft: 24, paddingRight: 24, maxWidth: 1150, margin: "0 auto", color: P.text },
  h1: { fontSize: 20, fontWeight: 700, color: P.text, margin: 0 },
  sub: { fontSize: 12, color: P.textMuted, marginTop: 2 },
  btnGhost: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "6px 14px", color: P.textSec, fontSize: 11, cursor: "pointer", fontWeight: 600 },
  input: { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, padding: "6px 10px", color: P.text, fontSize: 12, outline: "none" },
  card: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 16 },
};
const chip = (color, text) => (
  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, color, background: `${color}1f`, border: `1px solid ${color}55` }}>{text}</span>
);

export default function VeilleView({ L, lang, onBack }) {
  const t = TXT[lang] || TXT.fr;
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [tab, setTab] = useState("liste");      // liste | lignee
  const [selected, setSelected] = useState(null); // fiche en détail (drill-down)

  // filtres Liste
  const [fTheme, setFTheme] = useState("");
  const [fCourt, setFCourt] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [q, setQ] = useState("");

  // lignée
  const [ligTheme, setLigTheme] = useState(THEME_META[0].label);

  // note de lignée
  const [notes, setNotes] = useState([]);
  const [inclureSec, setInclureSec] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genErr, setGenErr] = useState(null);
  const [openNote, setOpenNote] = useState(null);

  // ── chargement unique du corpus (pagination jusqu'à épuisement) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let all = [], offset = 0;
        for (;;) {
          const res = await fetch(`${API_BASE}/api/jurisprudence/fiches?limit=200&offset=${offset}`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          all = all.concat(data.fiches || []);
          if (!data.fiches || data.fiches.length === 0 || all.length >= (data.total || 0)) break;
          offset += data.fiches.length;
        }
        if (!cancelled) { setFiches(all); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setErr(String(e.message || e)); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── filtrage client-side (Liste) ──
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return fiches.filter((f) => {
      if (fTheme && f.theme_principal !== fTheme) return false;
      if (fCourt && classifyJuridiction(f.juridiction) !== fCourt) return false;
      if (fFrom && (!f.date_decision || f.date_decision < fFrom)) return false;
      if (fTo && (!f.date_decision || f.date_decision > fTo)) return false;
      if (ql) {
        const blob = [f.parties, f.faits, f.solution, f.positionnement_lignee, f.juridiction, f.numero, f.theme_principal]
          .filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(ql)) return false;
      }
      return true;
    });
  }, [fiches, fTheme, fCourt, fFrom, fTo, q]);

  // ── fiches de la lignée (thème choisi, ordre CHRONOLOGIQUE CROISSANT) ──
  const ligFiches = useMemo(
    () => fiches.filter((f) => f.theme_principal === ligTheme)
      .slice().sort((a, b) => (a.date_decision || "").localeCompare(b.date_decision || "")),
    [fiches, ligTheme]
  );

  // historique des notes du thème de lignée (rechargé au changement de thème)
  useEffect(() => {
    let cancelled = false;
    setOpenNote(null);
    setGenErr(null);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jurisprudence/notes-lignee?theme=${encodeURIComponent(ligTheme)}`);
        const data = await res.json();
        if (!cancelled) setNotes(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setNotes([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ligTheme]);

  const genererNote = async () => {
    setGenLoading(true);
    setGenErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/jurisprudence/note-lignee`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme: ligTheme, inclure_secondaires: inclureSec }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const note = await res.json();
      setNotes((prev) => [note, ...prev]);   // append-only : la plus récente en tête
      setOpenNote(note);
    } catch (e) {
      setGenErr(String(e.message || e));
    } finally {
      setGenLoading(false);
    }
  };

  const downloadNote = (note) => {
    const blob = new Blob([note.note_markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `note-lignee-${note.id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── en-tête commun ──
  const Header = (
    <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div>
        <h1 style={S.h1}>📚 {t.title}</h1>
        <div style={S.sub}>{t.subtitle}{!loading && !err ? ` · ${fiches.length}` : ""}</div>
      </div>
      <button style={S.btnGhost} onClick={onBack}>{L?.back || t.back_app}</button>
    </nav>
  );

  if (loading) return <div>{Header}<div style={{ ...S.wrap, paddingTop: 48, paddingBottom: 48, textAlign: "center", color: P.textMuted }}>{t.loading}</div></div>;
  if (err) return <div>{Header}<div style={{ ...S.wrap, paddingTop: 48, paddingBottom: 48, textAlign: "center", color: "#ef4444" }}>{t.error} — {err}</div></div>;

  // ── DÉTAIL plein écran (drill-down depuis Liste ou Lignée) ──
  if (selected) {
    const f = selected;
    const tm = themeMeta(f.theme_principal);
    const pm = porteeMeta(f.portee);
    const Field = ({ label, children }) => (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: P.textMuted, marginBottom: 4, fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{children || t.none}</div>
      </div>
    );
    return (
      <div>
        {Header}
        <div style={{ ...S.wrap, paddingTop: 16, paddingBottom: 48 }}>
          <button style={{ ...S.btnGhost, marginBottom: 16 }} onClick={() => setSelected(null)}>{t.back_list}</button>
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: P.text }}>{f.juridiction}</div>
                <div style={{ fontSize: 12, color: P.textSec, marginTop: 2 }}>
                  {fmtDate(f.date_decision, lang)}{f.numero ? ` · n° ${f.numero}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {chip(tm.color, tm[lang])}
                {pm && chip(pm.color, pm[lang])}
              </div>
            </div>
            {f.troncature_signalee && (
              <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 7, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", color: "#fbbf24", fontSize: 11 }}>{t.d_troncature}</div>
            )}
            <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "16px 0" }} />
            {f.formation && <Field label={t.d_formation}>{f.formation}</Field>}
            <Field label={t.d_parties}>{f.parties}</Field>
            {f.themes_secondaires?.length > 0 && (
              <Field label={t.d_secondaires}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{f.themes_secondaires.map((s) => <span key={s}>{chip(themeMeta(s).color, themeMeta(s)[lang])}</span>)}</div>
              </Field>
            )}
            {f.actif_valorise && <Field label={t.d_actif}>{f.actif_valorise}</Field>}
            {f.methodes_valorisation?.length > 0 && (
              <Field label={t.d_methodes}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{f.methodes_valorisation.map((m, i) => <span key={i}>{chip("#C9A84C", m)}</span>)}</div>
              </Field>
            )}
            <Field label={t.d_faits}>{f.faits}</Field>
            <Field label={t.d_solution}>{f.solution}</Field>
            {f.positionnement_lignee && <Field label={t.d_lignee}>{f.positionnement_lignee}</Field>}
            {f.lien_legifrance && (
              <a href={f.lien_legifrance} target="_blank" rel="noopener noreferrer"
                 style={{ display: "inline-block", marginTop: 8, padding: "8px 14px", borderRadius: 8, background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.35)", color: "#C9A84C", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                {t.d_legifrance}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── sous-onglets + contenu ──
  const tabBtn = (k, label) => (
    <button onClick={() => setTab(k)} style={{
      background: tab === k ? "rgba(255,255,255,0.07)" : "transparent", border: "none",
      color: tab === k ? P.text : P.textMuted, borderBottom: tab === k ? "2px solid #C9A84C" : "2px solid transparent",
      padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
    }}>{label}</button>
  );

  return (
    <div>
      {Header}
      <div style={{ ...S.wrap, paddingTop: 12 }}>
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 16 }}>
          {tabBtn("liste", t.tab_liste)}
          {tabBtn("lignee", t.tab_lignee)}
        </div>

        {tab === "liste" && (
          <>
            {/* barre de filtres horizontale */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
              <select style={S.input} value={fTheme} onChange={(e) => setFTheme(e.target.value)}>
                <option value="">{t.all_themes}</option>
                {THEME_META.map((m) => <option key={m.label} value={m.label}>{m[lang]}</option>)}
              </select>
              <select style={S.input} value={fCourt} onChange={(e) => setFCourt(e.target.value)}>
                <option value="">{t.all_courts}</option>
                {COURT_FAMILIES.map((c) => <option key={c.key} value={c.key}>{c[lang]}</option>)}
              </select>
              <span style={{ fontSize: 11, color: P.textMuted }}>{t.f_from}</span>
              <input type="date" style={S.input} value={fFrom} onChange={(e) => setFFrom(e.target.value)} />
              <span style={{ fontSize: 11, color: P.textMuted }}>{t.f_to}</span>
              <input type="date" style={S.input} value={fTo} onChange={(e) => setFTo(e.target.value)} />
              <input style={{ ...S.input, flex: 1, minWidth: 200 }} placeholder={t.f_search} value={q} onChange={(e) => setQ(e.target.value)} />
              <span style={{ fontSize: 11, color: P.textMuted, whiteSpace: "nowrap" }}>{t.count(filtered.length, fiches.length)}</span>
            </div>

            {/* tableau dense */}
            {filtered.length === 0 ? (
              <div style={{ ...S.card, textAlign: "center", color: P.textMuted }}>{t.empty}</div>
            ) : (
              <div style={{ ...S.card, padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 720 }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.03)", color: P.textMuted, textAlign: "left" }}>
                      {[t.c_date, t.c_court, t.c_num, t.c_parties, t.c_theme, t.c_portee].map((h, i) => (
                        <th key={i} style={{ padding: "8px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((f) => {
                      const tm = themeMeta(f.theme_principal);
                      const pm = porteeMeta(f.portee);
                      return (
                        <tr key={f.id} onClick={() => setSelected(f)}
                          style={{ cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.03)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                          <td style={{ padding: "7px 10px", color: "#cbd5e1", whiteSpace: "nowrap" }}>{fmtDate(f.date_decision, lang)}</td>
                          <td style={{ padding: "7px 10px", color: P.textSec, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.juridiction}>{f.juridiction}</td>
                          <td style={{ padding: "7px 10px", color: P.textMuted, whiteSpace: "nowrap" }}>{f.numero || "—"}</td>
                          <td style={{ padding: "7px 10px", color: "#cbd5e1", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.parties}>{f.parties || "—"}</td>
                          <td style={{ padding: "7px 10px" }}>{chip(tm.color, tm[lang])}</td>
                          <td style={{ padding: "7px 10px" }}>{pm ? chip(pm.color, pm[lang]) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "lignee" && (
          <>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: P.textMuted }}>{t.lig_pick}</span>
              <select style={S.input} value={ligTheme} onChange={(e) => setLigTheme(e.target.value)}>
                {THEME_META.map((m) => <option key={m.label} value={m.label}>{m[lang]}</option>)}
              </select>
              {/* légende portée */}
              <span style={{ fontSize: 11, color: P.textMuted, marginLeft: 8 }}>{t.lig_legend}</span>
              {Object.values(PORTEE_META).map((p) => <span key={p.fr}>{chip(p.color, p[lang])}</span>)}
            </div>
            <div style={{ fontSize: 11, color: P.textMuted, marginBottom: 16, fontStyle: "italic" }}>{t.lig_hint}</div>

            {/* ── Note de lignée ── */}
            <style>{"@keyframes hvspin{to{transform:rotate(360deg)}}"}</style>
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: P.text }}>📝 {t.note_title}</span>
                <label style={{ fontSize: 11, color: P.textSec, display: "flex", alignItems: "center", gap: 5, cursor: genLoading ? "not-allowed" : "pointer" }}>
                  <input type="checkbox" checked={inclureSec} disabled={genLoading} onChange={(e) => setInclureSec(e.target.checked)} />
                  {t.note_sec}
                </label>
                <button onClick={genererNote} disabled={genLoading}
                  style={{ ...S.btnGhost, color: "#C9A84C", borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.12)", opacity: genLoading ? 0.6 : 1, cursor: genLoading ? "not-allowed" : "pointer" }}>
                  {genLoading ? t.note_generating : t.note_generate}
                </button>
                {genErr && <span style={{ fontSize: 11, color: "#ef4444" }}>{t.note_err} — {genErr}</span>}
              </div>

              {genLoading && (
                <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 7, background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.2)", color: "#C9A84C", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid rgba(201,168,76,0.3)", borderTop: "2px solid #C9A84C", borderRadius: "50%", animation: "hvspin 0.8s linear infinite" }} />
                  {t.note_generating_hint}
                </div>
              )}

              {notes.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: P.textMuted, marginBottom: 6, fontWeight: 700 }}>{t.note_history}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {notes.map((n) => {
                      const sel = openNote && openNote.id === n.id;
                      return (
                        <div key={n.id} onClick={() => setOpenNote(n)}
                          style={{ cursor: "pointer", padding: "6px 10px", borderRadius: 6, fontSize: 11, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center",
                            border: `1px solid ${sel ? "rgba(201,168,76,0.4)" : "rgba(255,255,255,0.06)"}`, background: sel ? "rgba(201,168,76,0.08)" : "transparent" }}>
                          <span style={{ color: "#cbd5e1", fontWeight: 600 }}>{fmtDateTime(n.created_at, lang)}</span>
                          <span style={{ color: P.textMuted }}>{n.nb_fiches} {t.note_fiches}</span>
                          <span style={{ color: P.textMuted }}>{t.note_sec_short} : {n.inclure_secondaires ? t.yes : t.no}</span>
                          {n.cost_usd != null && <span style={{ color: P.textMuted }}>${Number(n.cost_usd).toFixed(2)}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {openNote && (
                <div style={{ marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 11, color: P.textMuted }}>
                      {t.note_perimetre} : <span style={{ color: P.textSec }}>{fmtDateTime(openNote.created_at, lang)} · {openNote.nb_fiches} {t.note_fiches} · {t.note_sec_short} {openNote.inclure_secondaires ? t.yes : t.no}{openNote.model ? ` · ${openNote.model}` : ""}</span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => downloadNote(openNote)} style={S.btnGhost}>{t.note_download}</button>
                      <button onClick={() => setOpenNote(null)} style={S.btnGhost}>{t.note_close}</button>
                    </div>
                  </div>
                  <div style={{ ...S.card, background: "rgba(255,255,255,0.02)" }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{openNote.note_markdown}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            {ligFiches.length === 0 ? (
              <div style={{ ...S.card, textAlign: "center", color: P.textMuted }}>{t.lig_empty}</div>
            ) : (
              <div style={{ position: "relative", paddingLeft: 28 }}>
                {/* ligne verticale de la timeline */}
                <div style={{ position: "absolute", left: 8, top: 4, bottom: 4, width: 2, background: "rgba(255,255,255,0.08)" }} />
                {ligFiches.map((f) => {
                  const pm = porteeMeta(f.portee);
                  const dot = pm ? pm.color : P.textMuted;
                  return (
                    <div key={f.id} onClick={() => setSelected(f)}
                      style={{ position: "relative", marginBottom: 12, cursor: "pointer" }}>
                      <div style={{ position: "absolute", left: -28, top: 6, width: 10, height: 10, borderRadius: "50%", background: dot, border: "2px solid #0f172a", boxShadow: `0 0 0 2px ${dot}55` }} />
                      <div style={{ ...S.card, padding: 12, transition: "all 0.15s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)")}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)")}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: P.text, whiteSpace: "nowrap" }}>{fmtDate(f.date_decision, lang)}</span>
                          <span style={{ fontSize: 11, color: P.textSec, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.juridiction}</span>
                          {pm && chip(pm.color, pm[lang])}
                        </div>
                        {f.parties && <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 4 }}>{f.parties}</div>}
                        {f.solution && <div style={{ fontSize: 11, color: P.textSec, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{f.solution}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
